/**
 * Attachments (PRD §6.3) — the caps, the allowlist, the upload and the URL the
 * bytes come back down.
 *
 * Its own module since 2026-08-23, and for the same reason the api's screening
 * moved out of `knocks/`: there are TWO conversations that take files now — the
 * hearing, and the owner's own thread with their doorman — and a shared thing
 * filed under one of its callers is a shared thing somebody will fork. Split
 * out of `knock.ts`; the barrel re-exports, so no screen's import changed.
 */
import { API_URL, type RawError, nullableNum, num } from "./core";

/**
 * §6.3: 25 MB, and these eight extensions (`.txt` and `.md` since ruling
 * R-0905-3). Both numbers are the server's, kept here so the composer can
 * refuse a file it already knows will be refused — a round trip is a slow way
 * to say "that is a .zip".
 */
export const ATTACHMENT_MAX_BYTES = 26_214_400;

/** The server's tighter bound on a recording. Same reasoning, smaller number. */
export const AUDIO_MAX_BYTES = 8_388_608;

export const ATTACHMENT_EXTENSIONS = [
  "pdf",
  "docx",
  "pptx",
  "txt",
  "md",
  "png",
  "jpg",
  "jpeg",
] as const;

/**
 * The two a browser's own recorder writes (owner decision 2026-08-22).
 *
 * Kept apart from `ATTACHMENT_EXTENSIONS` on purpose: these are not offered in
 * the file picker. A voice note is something this product RECORDS, not
 * something a sender goes and finds on their disk — the picker showing
 * ".webm" would be an invitation to attach a screen recording, which the size
 * cap would then refuse after the upload.
 */
export const AUDIO_EXTENSIONS = ["webm", "m4a"] as const;

/** What the file picker is filtered to — the same list, as the input wants it. */
export const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.map((e) => `.${e}`).join(",");

/** Whether a stored attachment is something to render a player for. */
export function isAudioAttachment(attachment: Attachment): boolean {
  return attachment.mime_type.startsWith("audio/");
}

/**
 * Where the bytes are. Viewer-scoped and session-authorised on the server —
 * the id in this path is an argument, never a permission — so it is a plain
 * same-origin URL an `<audio src>` can use, with the cookie the browser
 * already has.
 */
export function attachmentUrl(attachmentId: string): string {
  return `${API_URL}/api/v1/attachment/${encodeURIComponent(attachmentId)}`;
}

/** The extension is what both sides judge a file by (`attachmentKind`). */
export function attachmentExtension(filename: string): string {
  const at = filename.lastIndexOf(".");
  return at === -1 ? "" : filename.slice(at + 1).toLowerCase();
}

export function attachmentTypeAllowed(filename: string): boolean {
  return (ATTACHMENT_EXTENSIONS as readonly string[]).includes(attachmentExtension(filename));
}

export type Attachment = {
  attachment_id: string;
  filename: string;
  page_count: number;
  /** What it is. `audio/*` renders a player; everything else stays a name. */
  mime_type: string;
  /** How long a recording runs, per the recorder that made it. Null otherwise. */
  duration_ms: number | null;
  /**
   * What a voice note says (ruling R-0905-4) — drawn under the player, on both
   * sides of the door. Null for anything that is not audio, and for a note the
   * transcriber could not hear.
   */
  transcript: string | null;
};

/** The server's six fields, read defensively — an older payload is not a crash. */
export function toAttachment(raw: Record<string, unknown>, fallbackName = ""): Attachment | null {
  const id = typeof raw["attachment_id"] === "string" ? raw["attachment_id"] : "";
  if (!id) return null;
  const name =
    typeof raw["filename"] === "string" && raw["filename"] ? raw["filename"] : fallbackName;
  return {
    attachment_id: id,
    filename: name,
    page_count: num(raw["page_count"]),
    mime_type: typeof raw["mime_type"] === "string" ? raw["mime_type"] : "",
    duration_ms: nullableNum(raw["duration_ms"]),
    transcript:
      typeof raw["transcript"] === "string" && raw["transcript"].trim() ? raw["transcript"] : null,
  };
}

/**
 * Every way an upload is refused (§6.3). Each one gets its own sentence in the
 * thread, which is why they are kept apart rather than folded into a failure.
 */
export type AttachmentFailureCode =
  | "ATTACHMENT_TOO_LARGE"
  | "ATTACHMENT_UNSUPPORTED_TYPE"
  /**
   * The browser's own refusal of an iPhone photo (ruling R-0925-2 (4)): the
   * vision call reads PNG and JPEG and nothing else, so a HEIC is turned back
   * before it is sent, with the two formats it can be sent as. Never a code the
   * server answers — the server's refusal of the extension is the generic one.
   */
  | "ATTACHMENT_HEIC"
  | "ATTACHMENT_QUARANTINED"
  /**
   * The scan could not run (ruling R-0926-1 (2)): the file was refused because
   * nobody could look at it, not because of anything in it. A 503, and the
   * one refusal whose sentence says to send it again.
   */
  | "ATTACHMENT_SCAN_UNAVAILABLE"
  | "KNOCK_NOT_ACTIVE"
  | "VALIDATION_ERROR"
  | "UNAUTHENTICATED"
  | "KNOCK_NOT_FOUND"
  | "UNKNOWN";

export type AttachmentResult =
  { ok: true; attachment: Attachment } | { ok: false; code: AttachmentFailureCode };

/**
 * WHERE a file is being put — a hearing, or the owner's own doorman thread.
 *
 * The composer components (the attach sheet, the mic, the preview) never learn
 * this: they hand a `File` to whoever rendered them, and the SCREEN knows which
 * conversation it is. So the target travels with the request rather than being
 * baked into a function name, and there is one upload rather than two that must
 * be kept saying the same thing about caps and codes.
 */
export type UploadTarget = { knockId: string } | { doorman: true };

export function attachmentEndpoint(target: UploadTarget): string {
  return "doorman" in target
    ? `${API_URL}/api/v1/doorman/attachment`
    : `${API_URL}/api/v1/knock/${encodeURIComponent(target.knockId)}/attachment`;
}

/**
 * A file into a conversation (§6.3). Multipart, field name `file` — the name
 * both routes read (`body.file`). The content-type is deliberately NOT set: the
 * browser writes it, with the boundary, and a hand-written one loses the
 * boundary and the upload with it.
 *
 * `signal` is the sender changing their mind: dismissing the chip aborts the
 * request rather than waiting for a file nobody wants any more.
 */
export async function uploadAttachment(
  target: UploadTarget,
  file: File,
  signal?: AbortSignal,
  /**
   * A recording's length, in ms, as the recorder clocked it. Audio only, and
   * only ever a display hint — see the migration that added the column for why
   * taking the client's word for this one number is proportionate.
   */
  durationMs?: number | null,
): Promise<AttachmentResult> {
  const form = new FormData();
  form.append("file", file);
  if (typeof durationMs === "number" && Number.isFinite(durationMs) && durationMs > 0) {
    form.append("duration_ms", String(Math.round(durationMs)));
  }

  let res: Response;
  try {
    res = await fetch(attachmentEndpoint(target), {
      method: "POST",
      credentials: "include",
      body: form,
      ...(signal ? { signal } : {}),
    });
  } catch {
    return { ok: false, code: "UNKNOWN" }; // offline, aborted, API down
  }

  const body = (await res.json().catch(() => ({}))) as RawError & Record<string, unknown>;

  if (!res.ok) return { ok: false, code: attachmentFailure(res.status, body.error?.code) };

  // The server sanitises the name; what it answers with is what was stored.
  const attachment = toAttachment(body, file.name);
  if (!attachment) return { ok: false, code: "UNKNOWN" };
  return { ok: true, attachment };
}

/**
 * The code the server named, or the one its status implies. Both are read
 * because the size refusal has been both a 413 and a 400 `VALIDATION_ERROR`,
 * and a sender should get the same sentence either way.
 */
function attachmentFailure(status: number, code: string | undefined): AttachmentFailureCode {
  switch (code) {
    case "ATTACHMENT_TOO_LARGE":
    case "ATTACHMENT_UNSUPPORTED_TYPE":
    case "ATTACHMENT_QUARANTINED":
    case "ATTACHMENT_SCAN_UNAVAILABLE":
    case "KNOCK_NOT_ACTIVE":
    case "VALIDATION_ERROR":
      return code;
  }
  if (status === 401) return "UNAUTHENTICATED";
  if (status === 404) return "KNOCK_NOT_FOUND";
  if (status === 413) return "ATTACHMENT_TOO_LARGE";
  if (status === 415) return "ATTACHMENT_UNSUPPORTED_TYPE";
  if (status === 400) return "VALIDATION_ERROR";
  return "UNKNOWN";
}
